// Carriers-mode hook for the Logistics bucket (D3f). Wraps the D3c
// carriers backend (list / create / update) and owns:
//   - the filtered carriers list (carrier_kind + is_active filters)
//   - the create-carrier form state
//   - the in-place edit state (Plants-style: editingId + edit draft)
//   - the toggle-active action (a thin updateLogisticsCarrier call
//     so the row's chip flips without leaving the panel)
//
// Mirrors useLogisticsRoutesPanel's shape (navTick / newSignal /
// actionBusy / localError) so the panel orchestrator can reuse the
// same Refresh + "+ New" patterns.
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createLogisticsCarrier,
  listLogisticsCarriers,
  updateLogisticsCarrier,
  type LogiCarrier,
  type LogiCarrierKind,
} from "@/lib/logistics";
import {
  CARRIER_KINDS,
  carrierNameSort,
  type CarriersActiveFilter,
} from "./logisticsCarriersShared";

export type CarrierEditDraft = {
  name: string;
  carrier_kind: LogiCarrierKind;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  // Comma-separated for the input element; serialized to string[] on submit.
  coverage_areas_text: string;
  // Empty string for "leave null"; numeric for set; user-side validation
  // ensures the input parses to a finite positive number before send.
  capacity_linear_feet_text: string;
  // ISO date strings (yyyy-mm-dd from <input type="date">); empty for null.
  insurance_expires_at: string;
  contract_expires_at: string;
  notes: string;
};

function emptyDraft(): CarrierEditDraft {
  return {
    name: "",
    carrier_kind: "contracted",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    coverage_areas_text: "",
    capacity_linear_feet_text: "",
    insurance_expires_at: "",
    contract_expires_at: "",
    notes: "",
  };
}

function draftFromCarrier(c: LogiCarrier): CarrierEditDraft {
  return {
    name: c.name,
    carrier_kind: c.carrier_kind,
    contact_name: c.contact_name,
    contact_email: c.contact_email,
    contact_phone: c.contact_phone,
    coverage_areas_text: c.coverage_areas.join(", "),
    capacity_linear_feet_text:
      c.capacity_linear_feet === null ? "" : String(c.capacity_linear_feet),
    insurance_expires_at: c.insurance_expires_at ? c.insurance_expires_at.slice(0, 10) : "",
    contract_expires_at: c.contract_expires_at ? c.contract_expires_at.slice(0, 10) : "",
    notes: c.notes,
  };
}

function parseCoverageAreas(text: string): string[] {
  return text
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function parseCapacity(text: string): number | null | "invalid" {
  const t = text.trim();
  if (t === "") return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) return "invalid";
  return n;
}

export type CarriersListFilter = {
  carrier_kind: "" | LogiCarrierKind;
  is_active: CarriersActiveFilter;
};

export function useLogisticsCarriersPanel(navTick: number, newSignal: number) {
  const [carriers, setCarriers] = useState<LogiCarrier[]>([]);
  const [listBusy, setListBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const [filter, setFilter] = useState<CarriersListFilter>({
    carrier_kind: "",
    is_active: "",
  });

  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<CarrierEditDraft>(emptyDraft);
  const [newIsActive, setNewIsActive] = useState(true);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [edit, setEdit] = useState<CarrierEditDraft>(emptyDraft);

  const reload = useCallback(async () => {
    setListBusy(true);
    setLocalError(null);
    const apiFilters: { carrier_kind?: LogiCarrierKind; is_active?: boolean } = {};
    if (filter.carrier_kind) apiFilters.carrier_kind = filter.carrier_kind;
    if (filter.is_active === "true") apiFilters.is_active = true;
    else if (filter.is_active === "false") apiFilters.is_active = false;
    const res = await listLogisticsCarriers(apiFilters);
    setListBusy(false);
    if (!res.ok) {
      setCarriers([]);
      setLocalError(res.error);
      return;
    }
    const list = Array.isArray(res.data.carriers) ? res.data.carriers : [];
    setCarriers([...list].sort(carrierNameSort));
  }, [filter.carrier_kind, filter.is_active]);

  useEffect(() => {
    void reload();
  }, [reload, navTick]);

  useEffect(() => {
    if (newSignal === 0) return;
    setCreating(true);
    setEditingId(null);
    setDraft(emptyDraft());
    setNewIsActive(true);
    setLocalError(null);
  }, [newSignal]);

  const cancelCreate = useCallback(() => {
    setCreating(false);
    setLocalError(null);
  }, []);

  const submitCreate = useCallback(async () => {
    const name = draft.name.trim();
    if (!name) {
      setLocalError("Name is required.");
      return;
    }
    const capacity = parseCapacity(draft.capacity_linear_feet_text);
    if (capacity === "invalid") {
      setLocalError("Capacity (linear feet) must be a positive number.");
      return;
    }
    setActionBusy(true);
    setLocalError(null);
    const res = await createLogisticsCarrier({
      name,
      carrier_kind: draft.carrier_kind,
      contact_name: draft.contact_name.trim() || undefined,
      contact_email: draft.contact_email.trim() || undefined,
      contact_phone: draft.contact_phone.trim() || undefined,
      coverage_areas: parseCoverageAreas(draft.coverage_areas_text),
      capacity_linear_feet: capacity === null ? undefined : capacity,
      insurance_expires_at: draft.insurance_expires_at.trim() || undefined,
      contract_expires_at: draft.contract_expires_at.trim() || undefined,
      is_active: newIsActive,
      notes: draft.notes.trim() || undefined,
    });
    setActionBusy(false);
    if (!res.ok) {
      setLocalError(res.error);
      return;
    }
    setCreating(false);
    await reload();
  }, [draft, newIsActive, reload]);

  const startEdit = useCallback((carrier: LogiCarrier) => {
    setEditingId(carrier.id);
    setEdit(draftFromCarrier(carrier));
    setCreating(false);
    setLocalError(null);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setLocalError(null);
  }, []);

  const submitEdit = useCallback(async () => {
    if (!editingId) return;
    const name = edit.name.trim();
    if (!name) {
      setLocalError("Name cannot be blank.");
      return;
    }
    const capacity = parseCapacity(edit.capacity_linear_feet_text);
    if (capacity === "invalid") {
      setLocalError("Capacity (linear feet) must be a positive number.");
      return;
    }
    setActionBusy(true);
    setLocalError(null);
    const res = await updateLogisticsCarrier({
      carrier_id: editingId,
      name,
      carrier_kind: edit.carrier_kind,
      contact_name: edit.contact_name,
      contact_email: edit.contact_email,
      contact_phone: edit.contact_phone,
      coverage_areas: parseCoverageAreas(edit.coverage_areas_text),
      capacity_linear_feet: capacity,
      // Empty string clears the date (service interprets undefined as
      // "leave alone" and null as "clear", per D3c). The trim() guards
      // against accidental whitespace-only inputs.
      insurance_expires_at: edit.insurance_expires_at.trim() ? edit.insurance_expires_at.trim() : null,
      contract_expires_at: edit.contract_expires_at.trim() ? edit.contract_expires_at.trim() : null,
      notes: edit.notes,
    });
    setActionBusy(false);
    if (!res.ok) {
      setLocalError(res.error);
      return;
    }
    setEditingId(null);
    await reload();
  }, [editingId, edit, reload]);

  const toggleActive = useCallback(
    async (carrier: LogiCarrier) => {
      setActionBusy(true);
      setLocalError(null);
      const res = await updateLogisticsCarrier({
        carrier_id: carrier.id,
        is_active: !carrier.is_active,
      });
      setActionBusy(false);
      if (!res.ok) {
        setLocalError(res.error);
        return;
      }
      await reload();
    },
    [reload]
  );

  const kindOptions = useMemo(() => CARRIER_KINDS, []);

  return {
    carriers,
    listBusy,
    actionBusy,
    localError,
    filter,
    setFilter,
    creating,
    draft,
    setDraft,
    newIsActive,
    setNewIsActive,
    cancelCreate,
    submitCreate,
    editingId,
    edit,
    setEdit,
    startEdit,
    cancelEdit,
    submitEdit,
    toggleActive,
    reload,
    kindOptions,
  };
}
